import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Plus, X, Edit3, Trash2, Eye, Save, ToggleLeft, ToggleRight, Loader2, Sparkles, Tag } from "lucide-react";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { logError } from "../lib/log";

const CATEGORIES = [
  { key: "info_request", label: "Information request" },
  { key: "payment_request", label: "Payment request" },
  { key: "lodgement_confirmation", label: "Lodgement confirmation" },
  { key: "additional_fee", label: "Additional fee" },
  { key: "follow_up", label: "Follow-up" },
  { key: "deferred_ack", label: "Deferred acknowledgement" },
  { key: "engagement_welcome", label: "Engagement welcome" },
  { key: "refund_notice", label: "Refund notice" },
  { key: "document_stall", label: "Document stall" },
  { key: "generic", label: "Generic" },
];

const INPUT_CLS = "w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px] focus:outline-none focus:border-[var(--ink)]";

export default function EmailTemplates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/email-templates`);
      setRows(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (tpl) => {
    if (tpl.is_seed) { toast.error("Seed templates can be disabled but not deleted"); return; }
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE}/email-templates/${tpl.template_id}`);
      toast.success("Template deleted"); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleEnabled = async (tpl) => {
    try {
      await axios.put(`${API_BASE}/email-templates/${tpl.template_id}`, {
        name: tpl.name, category: tpl.category, subject: tpl.subject,
        body: tpl.body, auto_fire_on: tpl.auto_fire_on, enabled: !tpl.enabled,
      });
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="px-10 py-12 max-w-[1400px]" data-testid="email-templates-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">Phase 1 · Communication</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Email <em className="italic" style={{ color: "var(--accent)" }}>templates</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Pre-written messages for every stage of the engagement. Use <code className="text-[var(--accent)]">{`{{client_name}}`}</code>, <code className="text-[var(--accent)]">{`{{amount}}`}</code>, <code className="text-[var(--accent)]">{`{{year}}`}</code> etc. to personalise. Templates marked "auto-fire" send themselves when a lead moves to that stage.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="template-create-btn">
          <Plus size={14}/> New template
        </button>
      </div>

      {loading ? (
        <div className="text-[var(--ink-3)] text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          {rows.map((t) => (
            <div key={t.template_id} className={`card p-4 ${!t.enabled ? "opacity-50" : ""}`} data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[15px] truncate">{t.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mt-0.5">
                    {CATEGORIES.find(c => c.key === t.category)?.label || t.category}
                    {t.is_seed && <span className="ml-2 text-[var(--moss)]">· seed</span>}
                  </div>
                </div>
                {t.auto_fire_on && (
                  <span className="badge text-[10px]" style={{ background: "var(--accent)", color: "var(--paper)" }}>
                    auto · {t.auto_fire_on}
                  </span>
                )}
              </div>
              <div className="text-[12px] text-[var(--ink-2)] line-clamp-2 mb-3">{t.subject}</div>
              <div className="text-[11px] text-[var(--ink-3)] line-clamp-3 font-mono whitespace-pre-wrap mb-3">
                {t.body.slice(0, 200)}{t.body.length > 200 ? "…" : ""}
              </div>
              {t.merge_fields?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3" data-testid="template-merge-fields">
                  {t.merge_fields.slice(0, 4).map((f) => (
                    <span key={f} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--subtle)", color: "var(--accent)" }}>{`{{${f}}}`}</span>
                  ))}
                  {t.merge_fields.length > 4 && <span className="text-[10px] text-[var(--ink-3)]">+{t.merge_fields.length - 4}</span>}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-[var(--border)]">
                <button onClick={() => setPreviewing(t)} className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-1" data-testid={`template-preview-${t.template_id}`}><Eye size={11}/> Preview</button>
                <button onClick={() => setEditing(t)} className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1" data-testid={`template-edit-${t.template_id}`}><Edit3 size={11}/> Edit</button>
                <button onClick={() => toggleEnabled(t)} className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] inline-flex items-center gap-1" data-testid={`template-toggle-${t.template_id}`}>
                  {t.enabled ? <ToggleRight size={14} style={{color:"var(--moss)"}}/> : <ToggleLeft size={14}/>}
                </button>
                {!t.is_seed && (
                  <button onClick={() => remove(t)} className="text-[11px] text-[var(--ink-3)] hover:text-[#B91C1C]" data-testid={`template-delete-${t.template_id}`}><Trash2 size={11}/></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {(editing || creating) && (
        <TemplateModal
          tpl={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); }}
        />
      )}
      {previewing && (
        <PreviewModal tpl={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

function TemplateModal({ tpl, onClose, onSaved }) {
  const [form, setForm] = useState(() => tpl ? { ...tpl } : {
    name: "", category: "generic", subject: "", body: "",
    auto_fire_on: null, enabled: true,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error("Name, subject and body are required"); return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name, category: form.category,
        subject: form.subject, body: form.body,
        auto_fire_on: form.auto_fire_on || null,
        enabled: form.enabled !== false,
      };
      if (tpl) {
        await axios.put(`${API_BASE}/email-templates/${tpl.template_id}`, payload);
        toast.success("Template updated");
      } else {
        await axios.post(`${API_BASE}/email-templates`, payload);
        toast.success("Template created");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--ink)]/40 flex items-center justify-center p-6" onClick={onClose} data-testid="template-modal">
      <div className="bg-[var(--paper)] rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="font-heading text-2xl" style={{ fontWeight: 500 }}>{tpl ? "Edit template" : "New template"}</h2>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="template-modal-close"><X size={18}/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="overline mb-1 text-[10px]">Name *</div>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INPUT_CLS} data-testid="template-name-input" autoFocus/>
            </label>
            <label className="block">
              <div className="overline mb-1 text-[10px]">Category</div>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={INPUT_CLS} data-testid="template-category-select">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <div className="overline mb-1 text-[10px]">Auto-fire trigger</div>
            <select value={form.auto_fire_on || ""} onChange={(e) => setForm({ ...form, auto_fire_on: e.target.value || null })} className={INPUT_CLS} data-testid="template-autofire-select">
              <option value="">Manual send only</option>
              <option value="lodged">Auto-fire on Lodged</option>
              <option value="deferred">Auto-fire on Deferred</option>
            </select>
            <div className="text-[10px] text-[var(--ink-3)] mt-1">Only ONE template per trigger should be enabled. We'll pick the first match.</div>
          </label>
          <label className="block">
            <div className="overline mb-1 text-[10px]">Subject *</div>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={INPUT_CLS} data-testid="template-subject-input"/>
          </label>
          <label className="block">
            <div className="overline mb-1 text-[10px]">Body *</div>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={12} className={`${INPUT_CLS} font-mono text-[12px]`} data-testid="template-body-input"/>
            <div className="text-[10px] text-[var(--ink-3)] mt-2">
              Merge fields: <code>{`{{client_name}}`}</code> · <code>{`{{firm_name}}`}</code> · <code>{`{{amount}}`}</code> · <code>{`{{year}}`}</code> · <code>{`{{follow_up_date}}`}</code> · <code>{`{{deferral_reason}}`}</code> · <code>{`{{tax_years}}`}</code>
            </div>
          </label>
        </div>
        <div className="p-6 border-t border-[var(--border)] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="template-modal-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="template-modal-save">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
            {busy ? "Saving…" : (tpl ? "Save changes" : "Create template")}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ tpl, onClose }) {
  const [preview, setPreview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/leads`);
        setLeads(r.data.slice(0, 50));
      } catch (err) { logError("email-templates:reload", err); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.post(`${API_BASE}/email-templates/${tpl.template_id}/preview`, leadId ? { lead_id: leadId } : {});
        setPreview(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [tpl.template_id, leadId]);

  return (
    <div className="fixed inset-0 z-50 bg-[var(--ink)]/40 flex items-center justify-center p-6" onClick={onClose} data-testid="template-preview-modal">
      <div className="bg-[var(--paper)] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="font-heading text-xl" style={{ fontWeight: 500 }}>Preview · {tpl.name}</h2>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]"><X size={18}/></button>
        </div>
        <div className="p-6 space-y-3">
          <label className="block">
            <div className="overline mb-1 text-[10px]">Render with which lead's data?</div>
            <select value={leadId} onChange={(e) => setLeadId(e.target.value)} className={INPUT_CLS} data-testid="preview-lead-select">
              <option value="">— Placeholders only —</option>
              {leads.map((l) => <option key={l.lead_id} value={l.lead_id}>{l.name}</option>)}
            </select>
          </label>
          {preview && (
            <>
              <div className="card p-3" style={{ background: "var(--subtle)" }}>
                <div className="overline mb-1 text-[10px]">Subject</div>
                <div className="text-[14px] font-medium">{preview.subject}</div>
              </div>
              <div className="card p-3" style={{ background: "var(--subtle)" }}>
                <div className="overline mb-1 text-[10px]">Body</div>
                <div className="text-[13px] whitespace-pre-wrap">{preview.body}</div>
              </div>
              {preview.missing?.length > 0 && (
                <div className="text-[11px]" style={{ color: "#B91C1C" }} data-testid="preview-missing">
                  ⚠ Unfilled merge fields: {preview.missing.map(f => `{{${f}}}`).join(", ")}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
